/* Render a ShareGrid to a PNG image suitable for clipboard / social
   share. Output mirrors the text grid one-for-one — header line, the
   same set of rows with the same per-cell coloring, footer line, and
   the optional Hard Mode line — but draws each filled square as a
   rounded rectangle on a solid background so the image reads as a
   "pretty share card" rather than a screenshot of emoji.

   The colors are fixed (matching the share emoji palette) regardless of
   theme, since the squares need to look like 🟩🟨⬛🟧🟦 even when the
   surrounding card is dark. The card background and the header/footer
   text colors do flip with theme. */

import type { GridCell, ShareGrid, SquareMark } from "./share";
import type { Theme } from "../theme";

// ── Palette ────────────────────────────────────────────────────────────
//
// Saturated cell colors stay constant across themes — they're the visual
// equivalent of the share emojis (🟩🟨⬛⬜🟧🟦), which always render the
// same colors regardless of OS theme. The background and foreground text
// flip per-theme to match the surrounding app.

const PALETTE_LIGHT: Record<SquareMark, string> = {
  green: "#6aaa64",
  yellow: "#c9b458",
  gray: "#787c7e",
  placeholder: "#d3d6da",
  hint: "#f5a623",
  spaces: "#4189dd",
};

const PALETTE_DARK: Record<SquareMark, string> = {
  green: "#6aaa64",
  yellow: "#c9b458",
  gray: "#3a3a3c",
  placeholder: "#787c7e",
  hint: "#f5a623",
  spaces: "#4189dd",
};

const BG_LIGHT = "#ffffff";
const BG_DARK = "#1a1a1b";
const FG_LIGHT = "#1a1a1b";
const FG_DARK = "#e7e7e7";

// ── Layout constants (CSS pixels — multiplied by devicePixelRatio at
//    paint time for crispness) ──────────────────────────────────────────

const CELL = 36;
const CELL_RADIUS = 5;
const CELL_GAP = 4;
const SPACE_W = 14;
const PUNCT_W = 22;
const ROW_GAP = 5;
const MARGIN = 32;
const SECTION_GAP = 18;

const FONT_STACK =
  '"Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, ' +
  '"Apple Color Emoji", "Segoe UI Emoji", system-ui, sans-serif';

const HEADER_FONT = `700 24px ${FONT_STACK}`;
const HEADER_LH = 28;
const FOOTER_FONT = `600 16px ${FONT_STACK}`;
const FOOTER_LH = 20;
const HARD_FONT = `700 30px ${FONT_STACK}`;
const HARD_LH = 36;
const PUNCT_FONT = `700 22px ${FONT_STACK}`;
const URL_FONT = `500 12px ${FONT_STACK}`;
const URL_LH = 14;
const URL_TEXT = "PalinDragon.com";

// ── Helpers ────────────────────────────────────────────────────────────

function rowWidth(row: readonly GridCell[]): number {
  if (row.length === 0) return 0;
  let w = 0;
  for (const c of row) {
    if (c.kind === "square") w += CELL;
    else if (c.kind === "space") w += SPACE_W;
    else w += PUNCT_W;
  }
  w += (row.length - 1) * CELL_GAP;
  return w;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

function measureLine(
  ctx: CanvasRenderingContext2D,
  font: string,
  text: string,
): number {
  ctx.font = font;
  return ctx.measureText(text).width;
}

// ── Renderer ───────────────────────────────────────────────────────────

/** Render a ShareGrid to a PNG Blob. Uses a transient canvas; never
    touches the DOM. The blob is `image/png` regardless of mode.
    Throws if `canvas.toBlob` returns null (extremely rare — happens on
    canvases too large to encode, which we don't approach in practice). */
export async function renderShareImageBlob(
  grid: ShareGrid,
  theme: Theme,
): Promise<Blob> {
  const palette = theme === "dark" ? PALETTE_DARK : PALETTE_LIGHT;
  const bg = theme === "dark" ? BG_DARK : BG_LIGHT;
  const fg = theme === "dark" ? FG_DARK : FG_LIGHT;

  // Pre-measure with a throwaway context so we can size the canvas
  // before allocating the real one.
  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d")!;
  const widestRow = grid.rows.reduce((w, r) => Math.max(w, rowWidth(r)), 0);
  const headerW = measureLine(measureCtx, HEADER_FONT, grid.header);
  const footerW = measureLine(measureCtx, FOOTER_FONT, grid.footer);
  const hardW = grid.hardMode
    ? measureLine(measureCtx, HARD_FONT, "🐲 HARD MODE SOLVE! 🐲")
    : 0;
  const contentW = Math.ceil(Math.max(widestRow, headerW, footerW, hardW));
  const cssW = contentW + MARGIN * 2;

  const rowsBlockH =
    grid.rows.length * CELL + Math.max(0, grid.rows.length - 1) * ROW_GAP;
  const cssH =
    MARGIN +
    HEADER_LH +
    SECTION_GAP +
    rowsBlockH +
    SECTION_GAP +
    FOOTER_LH +
    (grid.hardMode ? SECTION_GAP / 2 + HARD_LH : 0) +
    SECTION_GAP / 2 +
    URL_LH +
    MARGIN;

  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(cssW * dpr);
  canvas.height = Math.ceil(cssH * dpr);
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Background fill — clear, then the card color.
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, cssW, cssH);

  // Header
  let y = MARGIN;
  ctx.fillStyle = fg;
  ctx.font = HEADER_FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(grid.header, cssW / 2, y);
  y += HEADER_LH + SECTION_GAP;

  // Rows — each row is centered horizontally inside the card.
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  for (const row of grid.rows) {
    const w = rowWidth(row);
    let x = (cssW - w) / 2;
    for (let i = 0; i < row.length; i++) {
      const c = row[i]!;
      if (c.kind === "square") {
        ctx.fillStyle = palette[c.mark];
        roundRect(ctx, x, y, CELL, CELL, CELL_RADIUS);
        x += CELL;
      } else if (c.kind === "space") {
        x += SPACE_W;
      } else {
        ctx.fillStyle = fg;
        ctx.font = PUNCT_FONT;
        ctx.fillText(c.ch, x + PUNCT_W / 2, y + CELL / 2 + 1);
        x += PUNCT_W;
      }
      if (i < row.length - 1) x += CELL_GAP;
    }
    y += CELL + ROW_GAP;
  }
  y += SECTION_GAP - ROW_GAP;

  // Footer
  ctx.fillStyle = fg;
  ctx.font = FOOTER_FONT;
  ctx.textBaseline = "top";
  ctx.fillText(grid.footer, cssW / 2, y);
  y += FOOTER_LH;

  if (grid.hardMode) {
    y += SECTION_GAP / 2;
    ctx.font = HARD_FONT;
    ctx.fillText("🐲 HARD MODE SOLVE! 🐲", cssW / 2, y);
    y += HARD_LH;
  }

  // URL line — small type, anchored at the bottom of the card with a
  // half-section gap above it so it reads as a quiet attribution rather
  // than competing with the footer or hard-mode line.
  y += SECTION_GAP / 2;
  ctx.font = URL_FONT;
  ctx.fillText(URL_TEXT, cssW / 2, y);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob != null) resolve(blob);
      else reject(new Error("canvas.toBlob returned null"));
    }, "image/png");
  });
}
